import { useEffect, useState } from "react";
import { Gift, Trash2 } from "@/lib/icons";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export function RewardIdeas({ clientId }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", action: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setItems(null); api.get(`/clinician/client/${clientId}/reward-ideas`).then((r) => setItems(r.data.items)); }, [clientId]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/clinician/client/${clientId}/reward-ideas`, form);
      setItems((xs) => [r.data, ...xs]); setForm({ title: "", description: "", action: "" });
      toast.success("Added to their reward pool — the AI can offer it (or riff on it) next time.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/clinician/reward-ideas/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); };
  if (!items) return null;
  return (
    <section className="warm-card p-6" data-testid="reward-ideas">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-amber-700" /><h3 className="font-display text-lg font-bold text-stone-900">Reward ideas from you</h3><span className="text-xs text-stone-500">{items.length} in the pool</span></div>
      <p className="text-xs text-stone-500 mb-4">Drop a reward you know would land. It joins their pool: the AI may offer it directly on a future win or adapt it, and the member sees it labelled "Suggested by your coach".</p>
      <form onSubmit={add} className="grid sm:grid-cols-[1fr_1.4fr_1fr_auto] gap-2 items-start">
        <input required minLength={2} maxLength={80} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Record-shop browse)" data-testid="idea-title" className="px-3 py-2 rounded-xl border border-stone-300 text-sm outline-none focus:border-amber-600" />
        <input maxLength={300} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why it fits them (optional)" data-testid="idea-description" className="px-3 py-2 rounded-xl border border-stone-300 text-sm outline-none focus:border-amber-600" />
        <input maxLength={200} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} placeholder="How to do it (optional)" data-testid="idea-action" className="px-3 py-2 rounded-xl border border-stone-300 text-sm outline-none focus:border-amber-600" />
        <button type="submit" disabled={busy} data-testid="idea-add" className="amber-btn text-sm px-4 py-2">{busy ? "…" : "Add"}</button>
      </form>
      {items.length > 0 && (
        <ul className="mt-4 divide-y divide-stone-100">
          {items.map((i) => (
            <li key={i.id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`idea-${i.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-stone-900">{i.title} {i.used_count > 0 && <span className="text-[10px] font-mono text-emerald-700 ml-1">offered ×{i.used_count}</span>}</div>
                {(i.description || i.action) && <div className="text-xs text-stone-600 mt-0.5">{i.description}{i.description && i.action ? " · " : ""}{i.action}</div>}
              </div>
              <button onClick={() => del(i.id)} data-testid={`idea-delete-${i.id}`} className="text-stone-400 hover:text-red-700 p-1" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
